import { useEffect, useState, useCallback } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import { eur, fmtDate } from "../helpers";
import { Field } from "../components/FormField";
import WhySheet from "../components/WhySheet";
import ApartmentCheckboxFilter from "../components/ApartmentCheckboxFilter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BarChart, Bar, Line, ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import {
  Wallet, Landmark, TrendingUp, TrendingDown, PiggyBank, FileDown, FileSpreadsheet, Home,
  CalendarClock, ShieldCheck, HelpCircle, CalendarDays, LogIn, LogOut, CreditCard, Sparkles,
  Wrench, FileText, Image, QrCode, BookOpen, Boxes, Settings2, History, ExternalLink, BedDouble,
} from "lucide-react";

const ADMIN_ROLES = ["super_admin", "admin", "amministratore"];
const now = new Date();
const DRILLABLE = { gross_revenue: 1, ota_commission: 1, net_revenue: 1, direct_costs: 1 };

const EVENT_ICON = {
  booking: CalendarDays, checkin: LogIn, checkout: LogOut, payment: CreditCard, payout: Wallet,
  cleaning: Sparkles, maintenance: Wrench, report: FileText, media: Image, qr: QrCode,
  config: Settings2, "Welcome Guide": BookOpen, Inventario: Boxes,
};
const catColor = (c) => ({
  Prenotazioni: "#2563EB", "Check-in": "#059669", "Check-out": "#D97706", Pagamenti: "#7C3AED",
  Payout: "#059669", Pulizie: "#0891B2", Manutenzioni: "#DC2626", Report: "#475569",
  "Media Center": "#DB2777", "QR Center": "#4F46E5", "Welcome Guide": "#0D9488",
  Immobile: "#64748B", Upsell: "#9333EA", Inventario: "#CA8A04", Utenze: "#0891B2",
}[c] || "#64748B");

export default function OwnerPortal() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const [owners, setOwners] = useState([]);
  const [ownerId, setOwnerId] = useState("");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [dl, setDl] = useState("");
  const [why, setWhy] = useState(null); // {apartmentId, metric}
  const [record, setRecord] = useState(null);
  const [opSelApts, setOpSelApts] = useState([]);

  useEffect(() => {
    if (isAdmin) api.get("/owners").then((r) => { setOwners(r.data); if (r.data[0]) setOwnerId(r.data[0].id); }).catch(() => {});
  }, [isAdmin]);

  const monthLabel = (m) => new Date(2020, m - 1, 1).toLocaleString(lang === "it" ? "it-IT" : "en-US", { month: "long" });
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const baseParams = useCallback(() => (isAdmin ? { year, month, owner_id: ownerId } : { year, month }), [isAdmin, year, month, ownerId]);

  const load = useCallback(() => {
    if (isAdmin && !ownerId) return;
    setErr("");
    api.get("/owner-portal/dashboard", { params: baseParams() })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setErr(e.response?.data?.detail || t("noData")); });
  }, [isAdmin, ownerId, baseParams, t]);
  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    try {
      setDl(kind);
      const path = kind === "pdf" ? "/owner-portal/report.pdf" : "/owner-portal/statement.xlsx";
      const res = await api.get(path, { params: baseParams(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `${kind === "pdf" ? "report" : "statement"}_${year}_${String(month).padStart(2, "0")}.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { /* noop */ } finally { setDl(""); }
  };

  const openRecord = async (ev) => {
    if (!ev.record_type || !ev.record_id) {
      if (ev.doc_url) window.open(ev.doc_url, "_blank");
      return;
    }
    try {
      const r = await api.get(`/records/${ev.record_type}/${ev.record_id}`);
      setRecord({ ...r.data, doc_url: ev.doc_url });
    } catch { /* noop */ }
  };

  const cell = (a, metric, value, cls = "text-slate-600") => (
    DRILLABLE[metric]
      ? <button data-testid={`why-${metric}-${a.apartment_id}`} onClick={() => setWhy({ apartmentId: a.apartment_id, metric })}
          className={`inline-flex items-center gap-1 hover:underline ${cls}`}>{eur(value)}<HelpCircle size={12} className="opacity-50" /></button>
      : <span className={cls}>{eur(value)}</span>
  );

  const k = data?.kpis;
  const cards = k ? [
    { icon: Wallet, label: t("grossRevenue"), value: eur(k.gross_revenue), tone: "bg-primary text-primary-foreground" },
    { icon: Landmark, label: t("netPayout"), value: eur(k.owner_payout), tone: "bg-emerald-100 text-emerald-700" },
    { icon: TrendingUp, label: t("occupancy"), value: `${k.avg_occupancy}%`, tone: "bg-slate-100 text-slate-700" },
    { icon: PiggyBank, label: "Payout totali", value: eur(k.payout_total_all_time), tone: "bg-violet-100 text-violet-700" },
  ] : [];

  return (
    <div className="space-y-6 fade-in" data-testid="owner-portal">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm font-medium text-indigo-600"><Landmark size={16} /> Portale Proprietario Pro</div>
        <p className="max-w-2xl text-sm text-slate-500">Trasparenza totale sui tuoi immobili: economia, diario eventi e analisi. Nel rispetto del GDPR non sono mai mostrati dati personali degli ospiti.</p>
      </div>

      <Card className="flex flex-wrap items-end gap-4 border-slate-200 p-5">
        {isAdmin && (
          <Field label={t("selectOwner")} className="min-w-[220px]">
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger data-testid="op-owner"><SelectValue placeholder={t("selectOwner")} /></SelectTrigger>
              <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label={t("month")} className="min-w-[150px]">
          <Select value={String(month)} onValueChange={(v) => setMonth(+v)}>
            <SelectTrigger data-testid="op-month"><SelectValue /></SelectTrigger>
            <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)} className="capitalize">{monthLabel(m)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("year")} className="min-w-[110px]">
          <Select value={String(year)} onValueChange={(v) => setYear(+v)}>
            <SelectTrigger data-testid="op-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <div className="ml-auto flex items-end gap-2">
          {data && (data.apartments || []).length > 0 && (
            <ApartmentCheckboxFilter apartments={(data.apartments || []).map((a) => ({ id: a.apartment_id, name: a.name }))}
              selected={opSelApts} onChange={setOpSelApts} testid="op-filter-apartments" />
          )}
          <Button variant="outline" className="gap-2" data-testid="op-download-pdf" disabled={!data || !!dl} onClick={() => download("pdf")}><FileDown size={15} /> {dl === "pdf" ? "…" : "PDF"}</Button>
          <Button variant="outline" className="gap-2" data-testid="op-download-xlsx" disabled={!data || !!dl} onClick={() => download("xlsx")}><FileSpreadsheet size={15} /> {dl === "xlsx" ? "…" : "Excel"}</Button>
        </div>
      </Card>

      {err && <div data-testid="op-error" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{err}</div>}

      {data && (
        <>
          <div className="font-head text-lg font-semibold text-slate-900" data-testid="op-owner-name">
            {data.owner.name} <span className="text-sm font-normal capitalize text-slate-500">· {monthLabel(data.period.month)} {data.period.year}</span>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="op-tabs">
              <TabsTrigger value="overview" data-testid="op-tab-overview">Panoramica</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="op-tab-timeline">Timeline</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="op-tab-analytics">Analytics</TabsTrigger>
            </TabsList>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="mt-5 space-y-5">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="op-kpis">
                {cards.map((c) => (
                  <Card key={c.label} className="flex items-center gap-4 border-slate-200 p-5">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${c.tone}`}><c.icon size={20} /></div>
                    <div><div className="font-head text-2xl font-semibold text-slate-900">{c.value}</div><div className="text-xs text-slate-500">{c.label}</div></div>
                  </Card>
                ))}
              </div>

              <Card className="overflow-hidden border-slate-200">
                <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700"><Home size={16} /> {t("apartments")} <span className="text-xs font-normal text-slate-400">— clicca gli importi per il "Perché?"</span></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
                      <tr><th className="px-4 py-3">{t("apartment")}</th><th className="px-4 py-3">{t("nights")}</th><th className="px-4 py-3">{t("occupancy")}</th><th className="px-4 py-3">{t("grossRevenue")}</th><th className="px-4 py-3">Comm. OTA</th><th className="px-4 py-3">Ricavi netti</th><th className="px-4 py-3">Costi diretti</th><th className="px-4 py-3">{t("netPayout")}</th></tr>
                    </thead>
                    <tbody>
                      {data.apartments.filter((a) => !opSelApts.length || opSelApts.includes(a.apartment_id)).map((a) => (
                        <tr key={a.apartment_id} data-testid={`op-apt-${a.apartment_id}`} className="border-b border-slate-100">
                          <td className="px-4 py-3 font-medium text-slate-900">{a.name}</td>
                          <td className="px-4 py-3 text-slate-600">{a.nights}</td>
                          <td className="px-4 py-3 text-slate-600">{a.occupancy}%</td>
                          <td className="px-4 py-3">{cell(a, "gross_revenue", a.gross_revenue)}</td>
                          <td className="px-4 py-3">{cell(a, "ota_commission", a.ota_commission, "text-violet-700")}</td>
                          <td className="px-4 py-3">{cell(a, "net_revenue", a.net_revenue)}</td>
                          <td className="px-4 py-3">{cell(a, "direct_costs", a.direct_costs, "text-amber-700")}</td>
                          <td className="px-4 py-3 font-semibold text-emerald-700">{eur(a.owner_payout)}</td>
                        </tr>
                      ))}
                      {data.apartments.filter((a) => !opSelApts.length || opSelApts.includes(a.apartment_id)).length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </Card>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card className="overflow-hidden border-slate-200">
                  <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700"><CalendarClock size={16} /> Prossimi soggiorni</div>
                  <div className="max-h-80 overflow-y-auto">
                    {data.upcoming_checkins.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-400">Nessun soggiorno in arrivo.</p>}
                    {data.upcoming_checkins.map((u, i) => (
                      <div key={i} data-testid={`op-upcoming-${i}`} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm">
                        <div><div className="font-medium text-slate-800">{u.apartment}</div><div className="text-xs text-slate-500">{fmtDate(u.check_in)} → {fmtDate(u.check_out)} · {u.nights} notti · {u.guests} ospiti</div></div>
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs capitalize text-slate-600">{u.channel || "—"}</span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card className="overflow-hidden border-slate-200">
                  <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700"><Wallet size={16} /> Ultimi payout</div>
                  <div className="max-h-80 overflow-y-auto">
                    {data.recent_payouts.length === 0 && <p className="px-4 py-6 text-center text-sm text-slate-400">Nessun payout registrato.</p>}
                    {data.recent_payouts.map((p, i) => (
                      <div key={i} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-sm"><div className="text-slate-600">{fmtDate(p.date)} · <span className="capitalize">{p.method || "—"}</span></div><div className="font-semibold text-emerald-700">{eur(p.amount)}</div></div>
                    ))}
                  </div>
                </Card>
              </div>
            </TabsContent>

            {/* TIMELINE */}
            <TabsContent value="timeline" className="mt-5">
              <TimelineTab params={baseParams()} onOpen={openRecord} />
            </TabsContent>

            {/* ANALYTICS */}
            <TabsContent value="analytics" className="mt-5">
              <AnalyticsTab params={baseParams()} />
            </TabsContent>
          </Tabs>

          <div className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck size={14} /> Privacy GDPR: i dati personali degli ospiti non sono mai visibili in questo portale.</div>
        </>
      )}

      {why && <WhySheet apartmentId={why.apartmentId} metric={why.metric} year={year} month={month} open={!!why} onOpenChange={(o) => !o && setWhy(null)} onNavigate={() => {}} />}

      <Dialog open={!!record} onOpenChange={(o) => !o && setRecord(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="op-record-dialog">
          <DialogHeader><DialogTitle>{record?.title}</DialogTitle><DialogDescription>Documento originale collegato all'evento (dati ospite oscurati per privacy).</DialogDescription></DialogHeader>
          {record && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                {(record.fields || []).map((f, i) => (
                  <div key={i} className="flex justify-between border-b border-slate-100 py-1.5 text-sm"><span className="text-slate-500">{f.label}</span><span className={`font-medium text-slate-800 ${f.mono ? "font-mono text-xs" : ""}`}>{String(f.value)}</span></div>
                ))}
              </div>
              {(record.links || []).length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {record.links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700"><ExternalLink size={14} /> {l.label}</a>)}
                  {record.doc_url && <a href={record.doc_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700"><ExternalLink size={14} /> Allegato</a>}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Timeline ---------------- */
function TimelineTab({ params, onOpen }) {
  const [events, setEvents] = useState([]);
  const [apts, setApts] = useState([]);
  const [aptFilter, setAptFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = { ...params, limit: 200 };
    if (aptFilter !== "all") p.apartment_id = aptFilter;
    api.get("/owner-portal/timeline", { params: p })
      .then((r) => { setEvents(r.data.events); setApts(r.data.apartments); })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [params, aptFilter]);

  const dt = (s) => { try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

  return (
    <div className="space-y-4" data-testid="op-timeline">
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-500">Diario completo dell'immobile</span>
        <Select value={aptFilter} onValueChange={setAptFilter}>
          <SelectTrigger className="ml-auto w-56" data-testid="timeline-apt-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli immobili</SelectItem>
            {apts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading && <p className="py-8 text-center text-sm text-slate-400">Caricamento…</p>}
      {!loading && events.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Nessun evento registrato.</p>}

      <div className="relative space-y-1 pl-6">
        <div className="absolute bottom-2 left-[9px] top-2 w-px bg-slate-200" />
        {events.map((ev) => {
          const Icon = EVENT_ICON[ev.type] || EVENT_ICON[ev.category] || History;
          const clickable = ev.record_type || ev.doc_url;
          return (
            <button key={ev.id} data-testid={`timeline-event-${ev.type}`} disabled={!clickable} onClick={() => onOpen(ev)}
              className={`relative flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${clickable ? "hover:bg-slate-50" : "cursor-default"}`}>
              <span className="absolute -left-[15px] top-3 flex h-4 w-4 items-center justify-center rounded-full ring-4 ring-white" style={{ backgroundColor: catColor(ev.category) }} />
              <Icon size={16} className="mt-0.5 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-slate-800">{ev.title}</span>
                  <Badge variant="secondary" className="shrink-0 text-[10px]">{ev.category}</Badge>
                  {clickable && <HelpCircle size={12} className="shrink-0 text-slate-300" />}
                </div>
                <div className="truncate text-xs text-slate-500">{ev.description}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-xs text-slate-400">{dt(ev.at)}</div>
                {ev.amount ? <div className="text-xs font-semibold text-slate-700">{eur(ev.amount)}</div> : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Analytics ---------------- */
function Delta({ v }) {
  if (v === null || v === undefined) return <span className="text-xs text-slate-400">—</span>;
  const up = v >= 0;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${up ? "text-emerald-600" : "text-red-600"}`}>{up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{Math.abs(v)}%</span>;
}

function AnalyticsTab({ params }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/owner-portal/analytics", { params }).then((r) => setD(r.data)).catch(() => setD(null)); }, [params]);
  if (!d) return <p className="py-8 text-center text-sm text-slate-400">Caricamento…</p>;

  const kpis = [
    { icon: Wallet, label: "Ricavi periodo", value: eur(d.current.gross), delta: d.comparison.revenue },
    { icon: Landmark, label: "Margine netto", value: eur(d.current.net_margin), delta: null },
    { icon: TrendingUp, label: "Occupazione", value: `${d.current.occupancy}%`, delta: d.comparison.occupancy },
    { icon: CalendarDays, label: "Prenotazioni", value: d.current.bookings, delta: d.comparison.bookings },
    { icon: BedDouble, label: "Permanenza media", value: `${d.current.avg_stay} notti`, delta: d.comparison.avg_stay },
    { icon: CreditCard, label: "ADR", value: eur(d.current.adr), delta: d.comparison.adr },
    { icon: TrendingUp, label: "RevPAR", value: eur(d.current.revpar), delta: d.comparison.revpar },
    { icon: Wallet, label: "Costi periodo", value: eur(d.current.costs), delta: null },
  ];

  return (
    <div className="space-y-5" data-testid="op-analytics">
      <div className="text-xs text-slate-500">Confronto con il periodo precedente ({d.previous.label})</div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {kpis.map((c) => (
          <Card key={c.label} className="border-slate-200 p-4">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500"><c.icon size={14} /> {c.label}</div>
            <div className="mt-1.5 flex items-end justify-between"><div className="font-head text-xl font-semibold text-slate-900">{c.value}</div><Delta v={c.delta} /></div>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200 p-5">
        <div className="mb-4 text-sm font-semibold text-slate-700">Andamento ultimi 12 mesi — ricavi & occupazione</div>
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer>
            <ComposedChart data={d.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} width={45} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} width={35} unit="%" />
              <Tooltip formatter={(v, n) => (n === "occupancy" ? `${v}%` : eur(v))} />
              <Legend />
              <Bar yAxisId="l" dataKey="revenue" name="Ricavi" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="occupancy" name="occupancy" stroke="#059669" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
